import React from 'react'
import { EmissionWrapper, StyledDivider } from './style'
import { Co2SettingsType } from '~/api/services/reporting'
import { DataFilteredType } from '.'
import { Checkbox, Dropdown, Grid } from 'semantic-ui-react'
import { El } from '~/components/Layout/style'
import { useTranslation } from 'react-i18next'

interface Props {
  co2Settings: Co2SettingsType
  handleChangeDataFiltered: (data: DataFilteredType) => void
  isLoading: boolean
  dataFiltered: DataFilteredType
}

const EmisssionFilters:React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const {
    co2Settings,
    handleChangeDataFiltered,
    isLoading,
    dataFiltered
  } = props

  const selectAllRegionFilter = {
    name: t('All regions'),
    id: 'selectAllRegions'
  }

  return <EmissionWrapper>
    <div>
      <div className='title'>{'THG Emissionen (Bereiche)'}</div>
      <StyledDivider/>
      <div style={{ marginTop: 10 }}>
        {co2Settings?.resultTypes[0].map((resultType) => {
          return <Checkbox
            disabled={isLoading}
            key={resultType?.key}
            style={{ margin: '0.5rem' }}
            label={resultType?.label}
            checked={dataFiltered?.resultType === resultType?.key}
            onChange={(e, data) => {
              if(data.checked) {
                handleChangeDataFiltered({
                  ...dataFiltered,
                  resultType: resultType?.key
                })
              }
            }}
          />
        })}
      </div>
    </div>
    <div>
      <div className='title'>{'Bereiche'}</div>
      <StyledDivider/>
      <div style={{ marginTop: 10 }}>
        {co2Settings?.resultTypes[1].map((resultType) => {
          return <Checkbox
            key={resultType?.key}
            disabled={isLoading}
            style={{ margin: '0.5rem' }}
            label={resultType?.label}
            checked={dataFiltered?.resultType === resultType?.key}
            onChange={(e, data) => {
              handleChangeDataFiltered({
                ...dataFiltered,
                resultType: data.checked ? resultType?.key : ''
              })
            }}
          />
        })}
      </div>
    </div>
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'space-between'
      }}>
        <div className='title'>{'Filter nach:'}</div>
        <div>
          <El.PageHeaderActionButton style={{ marginBottom: '0.8em' }}
            onClick={() => handleChangeDataFiltered({
              ...dataFiltered,
              breeds: co2Settings?.breeds?.map(breed => breed.id),
              perimeter: 'all',
              housingTypes: co2Settings?.housingTypes?.map(type => type.key),
              customFilters: co2Settings?.customFilters?.map(customFilter => customFilter.id),
              administrativeDistrictId: ['selectAllRegions', ...co2Settings?.administrativeDistricts?.map(district => district.id)]
            })}
            disabled={isLoading}>
            {'Alle Filter'}
          </El.PageHeaderActionButton>
          <El.PageHeaderActionButton style={{ marginBottom: '0.8em' }}
            onClick={() => handleChangeDataFiltered({
              ...dataFiltered,
              resultType: 'sold_milk_economic_allocation_fpcm_idf',
              farmerId: '',
              breeds: [co2Settings?.selectedData?.breedId],
              perimeter: co2Settings?.selectedData?.perimeter,
              housingTypes: co2Settings?.selectedData?.housingType ? [co2Settings?.selectedData?.housingType] : [],
              customFilters: co2Settings?.selectedData?.customFilterId ? [co2Settings?.selectedData?.customFilterId] : [],
              administrativeDistrictId: co2Settings?.selectedData?.administrativeDistrictId ? [co2Settings?.selectedData?.administrativeDistrictId] : []
            })}
            disabled={isLoading}>
            {'Filter zurücksetzen'}
          </El.PageHeaderActionButton>
        </div>
      </div>
      <StyledDivider/>
    </div>
    <Grid columns={2} stackable style={{ position: 'relative', height: 400 }}>
      <StyledDivider vertical/>
      <Grid.Column>
        {co2Settings?.perimeters && <Grid.Row>
          <div style={{ marginBottom: '1em' }}>
            <div className='title'>{'Umkreis'}</div>
            <div style={{ marginTop: 10 }}>
              <Dropdown
                options={co2Settings?.perimeters.map(perimeter => ({ value: perimeter.key, text: perimeter.value }))}
                selection
                disabled={isLoading}
                selectOnBlur={false}
                value={dataFiltered?.perimeter}
                clearable
                onChange={(_, data) => {
                  handleChangeDataFiltered({
                    ...dataFiltered,
                    perimeter: data.value as string
                  })
                }}
              />
            </div>
          </div>
          <StyledDivider />
        </Grid.Row>}
        {co2Settings?.housingTypes && <Grid.Row style={{ marginTop: '1em' }}>
          <div style={{ marginBottom: '1em' }}>
            <div className='title'>{'Aufstallung'}</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {co2Settings?.housingTypes.map((housingType) => {
                const farmerHousingType = co2Settings?.selectedData?.housingType === housingType.key
                const fontWeight = farmerHousingType ? 'bold' : 'normal'
                return <Checkbox
                  key={housingType?.key}
                  style={{ margin: '0.5rem' }}
                  disabled={isLoading || farmerHousingType}
                  label={<label style={{ fontWeight }}>{housingType?.label}</label>}
                  checked={Boolean(dataFiltered?.housingTypes?.find(id => id === housingType?.key))}
                  onChange={(e, data) => {
                    if(data.checked) {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        housingTypes: [...dataFiltered.housingTypes, housingType?.key]
                      })
                    } else {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        housingTypes: dataFiltered.housingTypes.filter(id => id !== housingType?.key)
                      })
                    }
                  }}
                />
              })}
            </div>
          </div>
          <StyledDivider />
        </Grid.Row>}
        {co2Settings?.customFilters && <Grid.Row style={{ marginTop: '1em' }}>
          <div style={{ marginBottom: '1em' }}>
            <div className='title'>{'Milchmenge'}</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {co2Settings?.customFilters.map((customFilter) => {
                const farmerCustomFilter = co2Settings?.selectedData?.customFilterId === customFilter.id
                const fontWeight = farmerCustomFilter ? 'bold' : 'normal'
                return <Checkbox
                  key={customFilter?.id}
                  style={{ margin: '0.5rem' }}
                  disabled={isLoading || farmerCustomFilter}
                  label={<label style={{ fontWeight }}>{`${customFilter?.name} (${customFilter?.amountFrom} - ${customFilter?.amountTo})`}</label>}
                  checked={Boolean(dataFiltered?.customFilters?.find(id => id === customFilter?.id))}
                  onChange={(e, data) => {
                    if(data.checked) {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        customFilters: [...dataFiltered.customFilters, customFilter?.id]
                      })
                    } else {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        customFilters: dataFiltered.customFilters.filter(id => id !== customFilter?.id)
                      })
                    }
                  }}
                />
              })}
            </div>
          </div>
        </Grid.Row> }
      </Grid.Column>
      <Grid.Column>
        <Grid.Row>
          <div style={{ marginBottom: '1em' }}>
            <div className='title'>{'Rasse'}</div>
            <div style={{ marginTop: 10 }}>
              {co2Settings?.breeds.map((breed) => {
                const farmerMainBreed = co2Settings?.selectedData?.breedId === breed.id
                const fontWeight = farmerMainBreed ? 'bold' : 'normal'
                return <Checkbox
                  key={breed?.id}
                  disabled={isLoading || farmerMainBreed}
                  style={{ margin: '0.5rem' }}
                  label={<label style={{ fontWeight }}>{breed?.label}</label>}
                  checked={Boolean(dataFiltered?.breeds?.find(id => id === breed?.id))}
                  onChange={(e, data) => {
                    if(data.checked) {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        breeds: [...dataFiltered.breeds, breed?.id]
                      })
                    } else {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        breeds: dataFiltered.breeds.filter(id => id !== breed?.id)
                      })
                    }
                  }}
                />
              })}
            </div>
          </div>
          <StyledDivider />
        </Grid.Row>
        {co2Settings?.administrativeDistricts && <Grid.Row style={{ marginTop: '1em' }}>
          <div style={{ marginBottom: '1em' }}>
            <div className='title'>{'Region'}</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {[selectAllRegionFilter, ...co2Settings?.administrativeDistricts].map((administrativeDistrict) => {
                const farmersAdministrativeDistrict = co2Settings?.selectedData?.administrativeDistrictId === administrativeDistrict.id
                const fontWeight = farmersAdministrativeDistrict ? 'bold' : 'normal'
                return <Checkbox
                  key={administrativeDistrict?.id}
                  disabled={isLoading || farmersAdministrativeDistrict}
                  style={{ margin: '0.5rem' }}
                  label={<label style={{ fontWeight }}>{administrativeDistrict?.name}</label>}
                  checked={Boolean(dataFiltered?.administrativeDistrictId?.find(id => id === administrativeDistrict?.id))}
                  onChange={(e, data) => {
                    if(data.checked) {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        administrativeDistrictId: administrativeDistrict.id === 'selectAllRegions' ?
                          [selectAllRegionFilter, ...co2Settings?.administrativeDistricts].map(region => region.id) :
                          [...dataFiltered.administrativeDistrictId, administrativeDistrict?.id]
                      })
                    } else {
                      handleChangeDataFiltered({
                        ...dataFiltered,
                        administrativeDistrictId: administrativeDistrict.id === 'selectAllRegions' ?
                          co2Settings?.selectedData?.administrativeDistrictId ? [co2Settings?.selectedData?.administrativeDistrictId] : [] :
                          dataFiltered.administrativeDistrictId.filter(id => id !== administrativeDistrict?.id)
                      })
                    }
                  }}
                />
              })}
            </div>
          </div>
        </Grid.Row>}
      </Grid.Column>
    </Grid>
  </EmissionWrapper>
}
export default EmisssionFilters